import React, { useState, useMemo, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faPaperPlane,
  faRocket,
  faSearch,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  PageContainer,
  Header,
  MainContent,
  LibrarySection,
  ComposerSection,
  SectionTitle,
  FilterTabs,
  FilterButton,
  MessageList,
  ComposerForm,
  FormField,
  Label,
  Input,
  TextArea,
  Select,
  ButtonGroup,
  ActionButton,
  PreviewArea,
  PreviewTitle,
  SearchBar,
  EmptyState,
} from "./CommunicationsCenterStyle";
import MessageCard from "./MessageCard";
import SendModal from "./SendModal";
import messageService from "../../../Services/messageService";

const getInitialFormData = () => ({
  id: null,
  title: "",
  text: "",
  link: "",
  messageType: "AVISO",
  status: "DRAFT",
});

export default function CommunicationsCenterPage() {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [isSendModalOpen, setSendModalOpen] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());

  const fetchMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await messageService.getAllMessages();
      setMessages(data);
    } catch (error) {
      alert("Falha ao carregar as mensagens.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (message) => {
    setFormData({ ...message });
  };
  const handleClearComposer = () => {
    setFormData(getInitialFormData());
  };

  const handleSaveDraft = async () => {
    if (!formData.title || !formData.text) {
      alert("Título e Mensagem são obrigatórios para salvar.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        title: formData.title,
        text: formData.text,
        link: formData.link || null,
        messageType: formData.messageType,
      };
      if (formData.id && formData.status === "DRAFT") {
        await messageService.updateDraft(formData.id, payload);
      } else {
        await messageService.saveDraft(payload);
      }
      alert("Rascunho salvo com sucesso!");
      handleClearComposer();
      await fetchMessages();
    } catch (error) {
      console.error("Erro ao salvar rascunho:", error.response?.data || error);
      alert("Erro ao salvar rascunho.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (
      window.confirm(
        "Tem certeza que deseja excluir esta mensagem permanentemente?"
      )
    ) {
      try {
        await messageService.deleteMessage(messageId);
        setMessages(messages.filter((m) => m.id !== messageId));
        if (formData.id === messageId) {
          handleClearComposer();
        }
      } catch (error) {
        alert("Erro ao deletar mensagem.");
      }
    }
  };

  const handleOpenSendModal = async () => {
    if (!formData.title || !formData.text) {
      alert("Preencha o Título e a Mensagem antes de enviar.");
      return;
    }
    if (!formData.id) {
      setIsSaving(true);
      try {
        const payload = {
          title: formData.title,
          text: formData.text,
          link: formData.link || null,
          messageType: formData.messageType,
        };
        const savedDraft = await messageService.saveDraft(payload);
        setFormData(savedDraft);
        setSendModalOpen(true);
      } catch (error) {
        alert("Erro ao criar rascunho antes de enviar.");
      } finally {
        setIsSaving(false);
      }
    } else {
      setSendModalOpen(true);
    }
  };

  const handleConfirmSend = async (sendOptions) => {
    try {
      // Usando o serviço 'sendMessage' que não lida com agendamento
      await messageService.sendMessage(formData.id, sendOptions);
      alert("Mensagem enviada com sucesso!");
      handleClearComposer();
      await fetchMessages();
    } catch (error) {
      alert("Erro ao enviar mensagem.");
    }
  };

  const filteredMessages = useMemo(() => {
    return messages
      .filter((msg) => {
        if (activeFilter === "ALL") return true;
        return msg.status === activeFilter;
      })
      .filter(
        (msg) =>
          (msg.title &&
            msg.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (msg.text &&
            msg.text.toLowerCase().includes(searchTerm.toLowerCase()))
      );
  }, [messages, activeFilter, searchTerm]);

  return (
    <>
      <PageContainer>
        <Header>
          <h1>
            <FontAwesomeIcon icon={faRocket} /> Centro de Comunicações
          </h1>
          <p>
            Sua central para criar, gerenciar e enviar mensagens que engajam
            seus clientes.
          </p>
        </Header>

        <MainContent>
          <LibrarySection>
            <SectionTitle>Biblioteca de Mensagens</SectionTitle>
            <SearchBar>
              <FontAwesomeIcon icon={faSearch} />
              <input
                type="text"
                placeholder="Buscar por título ou conteúdo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
            <FilterTabs>
              <FilterButton
                active={activeFilter === "ALL"}
                onClick={() => setActiveFilter("ALL")}
              >
                Todas
              </FilterButton>
              <FilterButton
                active={activeFilter === "DRAFT"}
                onClick={() => setActiveFilter("DRAFT")}
              >
                Rascunhos
              </FilterButton>
              {/* O filtro para "Agendadas" foi removido */}
              <FilterButton
                active={activeFilter === "SENT"}
                onClick={() => setActiveFilter("SENT")}
              >
                Enviadas
              </FilterButton>
            </FilterTabs>
            <MessageList>
              {isLoading ? (
                <EmptyState>
                  <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                </EmptyState>
              ) : filteredMessages.length > 0 ? (
                filteredMessages.map((msg) => (
                  <MessageCard
                    key={msg.id}
                    message={msg}
                    onEdit={() => handleEdit(msg)}
                    onDelete={() => handleDelete(msg.id)}
                  />
                ))
              ) : (
                <EmptyState>
                  Nenhuma mensagem encontrada. <br /> Que tal criar uma agora?
                  ✨
                </EmptyState>
              )}
            </MessageList>
          </LibrarySection>

          <ComposerSection>
            <SectionTitle>
              {formData.id ? "Editando Mensagem" : "Nova Mensagem"}
            </SectionTitle>
            <ComposerForm>
              <FormField>
                <Label>Tipo de Mensagem</Label>
                <Select
                  name="messageType"
                  value={formData.messageType}
                  onChange={handleInputChange}
                >
                  <option value="AVISO">📢 Aviso</option>
                  <option value="PROMOCAO">🔥 Promoção</option>
                  <option value="ATUALIZACAO">⚙️ Atualização</option>
                  <option value="NOTICIA">📰 Notícia</option>
                </Select>
              </FormField>
              <FormField>
                <Label>Título</Label>
                <Input
                  type="text"
                  name="title"
                  placeholder="Ex: Manutenção Programada"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </FormField>
              <FormField>
                <Label>Mensagem</Label>
                <TextArea
                  name="text"
                  rows="6"
                  placeholder="Escreva aqui o conteúdo..."
                  value={formData.text}
                  onChange={handleInputChange}
                ></TextArea>
              </FormField>
              <FormField>
                <Label>Link (Opcional)</Label>
                <Input
                  type="text"
                  name="link"
                  placeholder="https://..."
                  value={formData.link}
                  onChange={handleInputChange}
                />
              </FormField>
              <ButtonGroup>
                <ActionButton
                  variant="secondary"
                  onClick={handleSaveDraft}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    "Salvar Rascunho"
                  )}
                </ActionButton>
                <ActionButton
                  variant="primary"
                  onClick={handleOpenSendModal}
                  disabled={isSaving}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />{" "}
                  {isSaving ? "Salvando..." : "Salvar e Enviar"}
                </ActionButton>
              </ButtonGroup>
              {formData.id && (
                <ActionButton
                  variant="tertiary"
                  onClick={handleClearComposer}
                  disabled={isSaving}
                >
                  <FontAwesomeIcon icon={faPlus} /> Criar Nova
                </ActionButton>
              )}
            </ComposerForm>
            <PreviewArea>
              <PreviewTitle>Preview em Tempo Real</PreviewTitle>
              <MessageCard message={formData} isPreview />
            </PreviewArea>
          </ComposerSection>
        </MainContent>
      </PageContainer>
      {isSendModalOpen && (
        <SendModal
          onClose={() => setSendModalOpen(false)}
          onConfirmSend={handleConfirmSend}
          message={formData}
        />
      )}
    </>
  );
}
